import { ReactNode, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTruckArrowRight,
  faGem,
  faGaugeHigh,
} from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { formatNumber, formatPercent } from "../../utilities/format";
import { dashboardsAjax } from "../../ajax/DashboardsAjax";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import useShiftTimes from "../Hooks/useShiftTimes";

export type KpiAccent = "text-theme-info" | "text-theme-success" | "text-theme-warning" | "text-theme-danger";

type KpiChipProps = {
  label: string;
  icon: IconDefinition;
  accent?: KpiAccent;
  value?: ReactNode;
  unit?: string;
  loading?: boolean;
  note?: ReactNode;
};

export const KpiChip: React.FC<KpiChipProps> = ({ label, icon, accent = "text-theme-info", value, unit, loading = false, note }) => (
  <div className="min-h-[70px] flex items-center justify-center sm:justify-start gap-3 px-4 py-3 sm:rounded-lg border-y sm:border-x border-theme-border bg-theme-surface-2 shadow-sm shadow-theme-shadow hover:shadow-md transition-all">
    <div className={`flex items-center justify-center w-10 h-10 rounded-full bg-theme-surface shrink-0 ${accent}`}>
      <FontAwesomeIcon icon={icon} className="text-lg" />
    </div>
    <div className="flex flex-col min-w-0">
      <span className="text-xs md:text-sm text-theme-text-muted uppercase tracking-wide truncate">
        {label}
      </span>
      {loading ? (
        <div className="animate-fade-in">
          <Skeleton animation="wave" variant="text" width={80} className="!bg-theme-surface !h-[22px] md:!h-[25px]" />
        </div>
      ) : (
        <span className="text-lg md:text-xl font-semibold text-theme-text leading-tight truncate animate-fade-in">
          {value ?? "—"}
          {value !== null && value !== undefined && unit ? (
            <span className="text-sm font-normal text-theme-text-muted ml-1">{unit}</span>
          ) : null}
        </span>
      )}
      {note && <span className="text-xs text-theme-text-muted truncate">{note}</span>}
    </div>
  </div>
);

const wholeNumber = (n?: number | null) => {
  if (n === null || n === undefined) return null;
  return formatNumber(Math.round(n));
};

export const DashboardKpiStrip: React.FC = () => {
  const { t } = useTranslation("dashboard");
  const { selectedDate, selectedShift } = useShiftTimes();
  const dateIso = selectedDate.toISODate();

  const { data: materialData, isPending: materialLoading } = useQuery({
    queryKey: ['dashboard', 'materialMovement', dateIso, selectedShift],
    queryFn: async () => {
      const res = await dashboardsAjax.getMaterialMovementCardData(selectedDate, selectedShift);
      if (ErrorResponse.isErrorResponse(res)) return null;
      return res;
    },
    enabled: selectedDate.isValid && !!selectedShift,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  const { data: plantData, isPending: plantLoading } = useQuery({
    queryKey: ['dashboard', 'plantStatus', dateIso, selectedShift],
    queryFn: async () => {
      const res = await dashboardsAjax.getPlantStatusCardData(selectedDate, selectedShift);
      if (ErrorResponse.isErrorResponse(res)) return null;
      return res;
    },
    enabled: selectedDate.isValid && !!selectedShift,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  const totalMovement = wholeNumber(materialData?.totalMaterialMovement?.both);
  const productionOre = useMemo(() => {
    const oreRow = materialData?.production?.rows?.find(r => r.label === "Ore");
    return wholeNumber(oreRow?.both);
  }, [materialData]);

  const avgAvailability = useMemo(() => {
    if (!plantData?.entries?.length) return null;
    return formatPercent(plantData.avgAvailability * 100, 0);
  }, [plantData]);

  return (
    <div className="w-full grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
      <KpiChip
        label={t("kpi.totalMovement")}
        icon={faTruckArrowRight}
        accent="text-theme-info"
        value={totalMovement}
        unit="t"
        loading={materialLoading}
      />
      <KpiChip
        label={t("kpi.productionOre")}
        icon={faGem}
        accent="text-theme-success"
        value={productionOre}
        unit="t"
        loading={materialLoading}
      />
      <KpiChip
        label={t("kpi.avgAvailability")}
        icon={faGaugeHigh}
        accent="text-theme-warning"
        value={avgAvailability}
        loading={plantLoading}
      />
    </div>
  );
};
